import { array, concat, fn } from "@ember/helper";
import { on } from "@ember/modifier";
import PluginOutlet from "discourse/components/plugin-outlet";
import bodyClass from "discourse/helpers/body-class";
import { eq, not } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import ModMarketBanner from "discourse/plugins/discourse-mod-market/discourse/components/mod-market-banner";
import ModMarketCard from "discourse/plugins/discourse-mod-market/discourse/components/mod-market-card";

export default <template>
  <main class="mod-market">
    {{bodyClass "mod-market-page"}}
    <h1 class="sr-only">{{i18n "discourse_mod_market.title"}}</h1>
    <ModMarketBanner @context="mods" />

    <section class="mod-market__catalog">
      <div class="mod-market__games-row">
        <nav
          class="mod-market__games"
          aria-label={{i18n "discourse_mod_market.filters.games"}}
        >
          <button
            type="button"
            class={{dConcatClass
              "mod-market__game"
              (unless @controller.game "is-selected")
            }}
            aria-pressed={{if @controller.game "false" "true"}}
            {{on "click" (fn @controller.selectGame null)}}
          >
            <span class="mod-market__game-icon --all" aria-hidden="true"></span>
            <span class="mod-market__game-name">{{i18n
                "discourse_mod_market.filters.all"
              }}</span>
            <span class="mod-market__game-count">{{@model.meta.catalog_total}}</span>
          </button>
          {{#each @model.facets.games as |game|}}
            <button
              type="button"
              class={{dConcatClass
                "mod-market__game"
                (if (eq @controller.game game.key) "is-selected")
              }}
              aria-pressed={{if (eq @controller.game game.key) "true" "false"}}
              disabled={{eq game.available false}}
              {{on "click" (fn @controller.selectGame game.key)}}
            >
              {{#if game.icon_url}}
                <img class="mod-market__game-icon" src={{game.icon_url}} alt="" />
              {{else}}
                <span class="mod-market__game-icon --fallback" aria-hidden="true">
                  {{dIcon "gamepad"}}
                </span>
              {{/if}}
              <span class="mod-market__game-name">{{game.name}}</span>
              <span class="mod-market__game-count">{{game.work_count}}</span>
              {{#if (eq game.available false)}}
                <span class="sr-only">{{i18n
                    "discourse_mod_market.games.coming_soon"
                  }}</span>
              {{/if}}
            </button>
          {{/each}}
        </nav>
        <PluginOutlet @name="after-mod-market-games" />
      </div>

      <div class="mod-market__result-header">
        <h2 id="mod-market-results-title">{{i18n
            "discourse_mod_market.catalog_title"
          }}</h2>
        <div
          class="mod-market__tabs"
          role="tablist"
          aria-label={{i18n "discourse_mod_market.tabs.label"}}
        >
          {{#each (array "all" "hot" "followed") as |tab|}}
            <button
              type="button"
              role="tab"
              id={{concat "mod-market-tab-" tab}}
              data-mod-market-tab={{tab}}
              class={{dConcatClass
                "mod-market__tab"
                (if (eq @controller.tab tab) "is-selected")
              }}
              aria-selected={{if (eq @controller.tab tab) "true" "false"}}
              aria-controls="mod-market-results-panel"
              tabindex={{if (eq @controller.tab tab) "0" "-1"}}
              {{on "click" (fn @controller.selectTab tab)}}
              {{on "keydown" (fn @controller.navigateTabs tab)}}
            >
              {{i18n (concat "discourse_mod_market.tabs." tab)}}
            </button>
          {{/each}}
        </div>
      </div>

      <div
        class={{dConcatClass
          "mod-market__layout"
          (if (eq @controller.tab "followed") "has-no-filters")
        }}
      >
        <section
          class="mod-market__results"
          aria-labelledby="mod-market-results-title"
        >
          <div
            id="mod-market-results-panel"
            role="tabpanel"
            aria-labelledby={{concat "mod-market-tab-" @controller.tab}}
          >
            {{#if @model.mods.length}}
              <div class="mod-market__grid">
                {{#each @model.mods as |mod|}}
                  <ModMarketCard
                    @mod={{mod}}
                    @capabilities={{@model.capabilities}}
                  />
                {{/each}}
              </div>
              <nav
                class="mod-market__pagination"
                aria-label={{i18n "discourse_mod_market.pagination.label"}}
              >
                <DButton
                  @action={{@controller.previousPage}}
                  @label="discourse_mod_market.pagination.previous"
                  @disabled={{not @controller.canGoBack}}
                  class="btn-default"
                />
                <span>{{i18n
                    "discourse_mod_market.pagination.current"
                    current=@controller.currentPage
                    total=@model.meta.total_pages
                  }}</span>
                <DButton
                  @action={{@controller.nextPage}}
                  @label="discourse_mod_market.pagination.next"
                  @disabled={{not @controller.canGoForward}}
                  class="btn-default"
                />
              </nav>
            {{else}}
              <div class="mod-market__empty">
                {{dIcon "magnifying-glass"}}
                <h3>{{i18n "discourse_mod_market.empty.title"}}</h3>
                <p>{{i18n "discourse_mod_market.empty.description"}}</p>
              </div>
            {{/if}}
          </div>
        </section>

        {{#unless (eq @controller.tab "followed")}}
          <aside
            class="mod-market-filter"
            aria-label={{i18n "discourse_mod_market.filters.label"}}
          >
            <label class="mod-market-filter__search">
              <span class="mod-market-filter__search-icon" aria-hidden="true">
                {{dIcon "magnifying-glass"}}
              </span>
              <span class="sr-only">{{i18n
                  "discourse_mod_market.filters.search"
                }}</span>
              <input
                class="mod-market-filter__search-input"
                data-test-mod-market-search
                type="search"
                value={{@controller.q}}
                placeholder={{i18n
                  "discourse_mod_market.filters.search_placeholder"
                }}
                {{on "blur" @controller.searchOnBlur}}
              />
            </label>

            <label class="mod-market-filter__character">
              <span class="mod-market-filter__character-icon" aria-hidden="true">
                {{dIcon "users"}}
              </span>
              <span class="sr-only">{{i18n
                  "discourse_mod_market.filters.character"
                }}</span>
              <select
                class="mod-market-filter__character-select"
                data-test-mod-market-character
                {{on "change" @controller.selectCharacter}}
              >
                <option value="" selected={{not @controller.character}}>{{i18n
                    "discourse_mod_market.filters.all_characters"
                  }}</option>
                {{#each @model.facets.characters as |character|}}
                  <option
                    value={{character.key}}
                    selected={{eq @controller.character character.key}}
                  >{{character.name}}</option>
                {{/each}}
              </select>
              <span class="mod-market-filter__character-arrow" aria-hidden="true"></span>
            </label>

            <section class="mod-market-filter__group">
              <h3 class="mod-market-filter__group-title">{{i18n
                  "discourse_mod_market.filters.type"
                }}</h3>
              <div class="mod-market-filter__options">
                <button
                  type="button"
                  data-test-mod-market-type="all"
                  class={{dConcatClass
                    "mod-market-filter__option"
                    (unless @controller.type "is-selected")
                  }}
                  aria-pressed={{if @controller.type "false" "true"}}
                  {{on "click" (fn @controller.selectType null)}}
                >
                  <span
                    class="mod-market-filter__option-icon"
                    data-filter-icon="all"
                    aria-hidden="true"
                  ></span>
                  <span>{{i18n "discourse_mod_market.filters.all_types"}}</span>
                </button>
                {{#each @model.facets.types as |type|}}
                  <button
                    type="button"
                    data-test-mod-market-type={{type.key}}
                    class={{dConcatClass
                      "mod-market-filter__option"
                      (if (eq @controller.type type.key) "is-selected")
                    }}
                    aria-pressed={{if
                      (eq @controller.type type.key)
                      "true"
                      "false"
                    }}
                    {{on "click" (fn @controller.selectType type.key)}}
                  >
                    {{#if type.icon_url}}
                      <img
                        class="mod-market-filter__option-icon --image"
                        src={{type.icon_url}}
                        alt=""
                        loading="lazy"
                      />
                    {{else}}
                      <span
                        class="mod-market-filter__option-icon"
                        data-filter-icon="ui"
                        aria-hidden="true"
                      ></span>
                    {{/if}}
                    <span>{{type.name}}</span>
                  </button>
                {{/each}}
              </div>
            </section>

            <section class="mod-market-filter__group">
              <h3 class="mod-market-filter__group-title">{{i18n
                  "discourse_mod_market.filters.sort"
                }}</h3>
              <div class="mod-market-filter__options">
                {{#each (array "latest" "downloads" "popular") as |sort|}}
                  <button
                    type="button"
                    data-test-mod-market-sort={{sort}}
                    class={{dConcatClass
                      "mod-market-filter__option"
                      (if (eq @controller.sort sort) "is-selected")
                    }}
                    aria-pressed={{if
                      (eq @controller.sort sort)
                      "true"
                      "false"
                    }}
                    {{on "click" (fn @controller.selectSort sort)}}
                  >
                    <span
                      class="mod-market-filter__option-icon"
                      data-filter-icon={{sort}}
                      aria-hidden="true"
                    ></span>
                    <span>{{i18n (concat "discourse_mod_market.sort." sort)}}</span>
                  </button>
                {{/each}}
              </div>
            </section>

          </aside>
        {{/unless}}
      </div>
    </section>
  </main>
</template>
